import React, { useCallback, useMemo } from 'react';
import { Box, BoxProps, TextV2, Flex, ButtonV2, SpacingStack, LinkV2, useDisclosure } from '@withjoy/joykit';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { FormattedEventScheduleFragment, EventScheduleFragment, Category } from '@graphql/generated';
import { ContentColumns } from '../../primitives';
import { AddToCalendar as AddToCalendarIcon, Direction } from '@withjoy/joykit/icons';
import { InfoBlock } from '../../primitives/InfoBlock';
import { DescriptionBlock, DressCodeBlock, MapBlock } from '../../components/ItineraryBlocks';
import { VirtualEventBlock } from '../../components/VirtualEventBlock';
import { variantStyles, VariantStyles } from './';
import { ScheduleListTestIds, useTestIds } from './ScheduleList.utils';
import { useTranslations, ScheduleListTranslations } from './ScheduleList.i18n';
import { AddToCalendar } from '@shared/components/AddToCalendar';
import { config } from '@static/js/env.config';
import { StyledVirtualEventContainer, styles } from '@apps/guest/packages/layout-engine/widgets/ScheduleList/ScheduleList.styles';
import { TelemetryProvider, useScheduleListTelemetry } from './ScheduleList.telemetry';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useScheduleListController } from '@apps/guest/packages/layout-engine/widgets/ScheduleList/ScheduleList.controller';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body3ToButtonsOverride,
  body3ToParagraphOverride,
  button2ToButtonsOverride,
  hed3ToSubHeadingOverride,
  hed4ToSubHeadingOverride,
  label6ToCaptionOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useColorPaletteProvider } from '../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonStylesWithAccentColor } from '../../layouts/layout.utils';
import { getGoogleMapsSearchLink } from '../../common/utils/getGoogleMapsSearchLink';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { pxToRem } from '@withjoy/joykit/theme';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useLayout } from '../../layouts/LayoutProvider';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { useEventAccommodationsCheck } from '@shared/hooks/useEventAccommodationsCheck';
import { AccommodationsConsoleConfigProvider } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import AccommodationsConsoleDialog from '@apps/accommodations';
import { useEventDisplayName } from '@shared/utils/eventInfo';

export type Variant = 'stack' | 'inline';

export interface ScheduleListProps
  extends Readonly<{
      // Add props here.
      eventId: string;
      eventHandle: string;
      variant?: Variant;
      layout?: string;
    }>,
    BoxProps {}

export interface ScheduleListContentProps
  extends Readonly<{
      eventId: string;
      formattedSchedule: ReadonlyArray<FormattedEventScheduleFragment>;
      tbaSchedule: ReadonlyArray<EventScheduleFragment>;
      eventHandle: string;
      variant?: Variant;
      layout?: string;
      shouldDisplayUnlockButton: Maybe<boolean>;
      handleUnlockDialogClick: () => void;
      unlockButtonText: string;
      isAccommodationPageVisible: boolean;
      onClickWhereToStay: () => void;
    }>,
    BoxProps {}

const PrimaryBlock: React.FC<
  Readonly<{
    eventId: string;
    isMobile?: boolean;
    timeframe?: string;
    eventHandle: string;
    title: Maybe<string>;
    placeId: Maybe<string>;
    showMap: Maybe<boolean>;
    location: Maybe<string>;
    dressCode: Maybe<string>;
    description: Maybe<string>;
    item?: EventScheduleFragment;
    calendarDescription?: string;
    eventNameMarkup?: JSX.Element;
    onClickWhereToStay: () => void;
    isToBeAnnounced: Maybe<boolean>;
    virtualEventMarkup?: JSX.Element;
    isAccommodationPageVisible: boolean;
    shouldShowWhereToStayButton: boolean;
    translations: ScheduleListTranslations;
    styleProps: VariantStyles['primaryBlock'];
    testIds: ScheduleListTestIds['listItemTestIds'];
    onAddressClicked?: (scheduleTitle: string) => void;
    onCalendarClicked?: (title: string, type: string) => void;
  }>
> = props => {
  const {
    title,
    description,
    dressCode,
    isToBeAnnounced,
    translations,
    eventNameMarkup,
    location,
    placeId,
    showMap,
    styleProps,
    testIds,
    virtualEventMarkup,
    onAddressClicked,
    timeframe,
    calendarDescription,
    isMobile,
    item,
    onCalendarClicked,
    eventHandle,
    isAccommodationPageVisible,
    onClickWhereToStay,
    shouldShowWhereToStayButton,
  } = props;
  const { accentColor } = useColorPaletteProvider();
  const calendarButtonStyles = useMemo(() => getCalendarButtonProps(accentColor), [accentColor]);
  const hotelRoutePaths = useHotelRoutePaths();
  const { layout } = useLayout();

  const [hideAddToCalendar, loadingAddToCalendarUserConsentKey] = useIsFeatureEnabled({ consentKey: 'scheduleOptOutAddToCalendar' });
  const [accommodationsConsoleOnScheduleExperiment] = useIsFeatureEnabled('accommodationsConsoleOnScheduleExperiment', {
    skip: isAccommodationPageVisible || !shouldShowWhereToStayButton,
  });

  // Sometimes people add empty text to description, need to check for content.
  if (!eventNameMarkup && !virtualEventMarkup && !description?.trim() && !isToBeAnnounced && !dressCode && !location) {
    return null;
  }

  return (
    <Box
      {...testIds.primaryBlock.prop}
      {...styleProps.container}
    >
      <InfoBlock.Group spread="md">
        {eventNameMarkup}
        {virtualEventMarkup}
        {description && <DescriptionBlock {...testIds.descriptionBlock.prop}>{description}</DescriptionBlock>}
        {isToBeAnnounced && !description && <DescriptionBlock {...testIds.descriptionBlock.prop}>{translations.toBeAnnouncedDescription}</DescriptionBlock>}
        {dressCode && <DressCodeBlock {...testIds.dressCodeblock.prop}>{dressCode}</DressCodeBlock>}
        {(location || placeId) && (
          <MapBlock
            handleClick={() => {
              if (onAddressClicked) {
                onAddressClicked(title ?? '');
              }
            }}
            {...testIds.mapBlock.prop}
            location={location}
            placeId={placeId}
            showMap={!!showMap}
          />
        )}
        <Flex
          gap={5}
          flexWrap={'wrap'}
          justifyContent={layout === 'brannan' ? 'flex-start' : 'center'}
        >
          <GuestSiteTypographyOverride override={body3ToParagraphOverride}>
            {!hideAddToCalendar && !loadingAddToCalendarUserConsentKey && timeframe && item?.startTime?.timestamp && (
              <AddToCalendar
                start={item?.startTime?.timestamp}
                end={item?.endTime?.timestamp}
                title={item?.name}
                location={item?.location ?? ''}
                description={calendarDescription}
                handleClickAddToCalendar={onCalendarClicked}
              >
                <ButtonV2
                  {...testIds.addToCalendar.prop}
                  variant="ghost"
                  shape="rounded"
                  {...calendarButtonStyles}
                  _hover={{ background: 'transparent', color: calendarButtonStyles.color }}
                  paddingX={0}
                  size="sm"
                  textDecoration={'underline'}
                  typographyVariant="body3"
                >
                  {translations.addToCalendar}
                </ButtonV2>
              </AddToCalendar>
            )}
            {shouldShowWhereToStayButton && (
              <>
                {accommodationsConsoleOnScheduleExperiment && !isAccommodationPageVisible ? (
                  <ButtonV2
                    {...testIds.getDirections.prop}
                    variant="ghost"
                    shape="rounded"
                    {...calendarButtonStyles}
                    _hover={{ backgroundColor: 'transparent', color: calendarButtonStyles.color }}
                    paddingX={0}
                    size="sm"
                    fontWeight={400}
                    textDecoration="underline"
                    onClick={onClickWhereToStay}
                  >
                    {translations.whereToStay}
                  </ButtonV2>
                ) : (
                  <ButtonV2
                    {...testIds.getDirections.prop}
                    variant="ghost"
                    shape="rounded"
                    {...calendarButtonStyles}
                    _hover={{ backgroundColor: 'transparent', color: calendarButtonStyles.color }}
                    paddingX={0}
                    size="sm"
                    fontWeight={400}
                  >
                    <LinkV2
                      href={hotelRoutePaths.bookYourStay.buildRelativeUrl(eventHandle, {
                        utm_medium: 'web',
                        utm_source: 'schedule_list_item',
                        utm_campaign: 'schedule_item_where_to_stay',
                      })}
                      isExternal={true}
                      color={calendarButtonStyles.color}
                      _hover={{ color: calendarButtonStyles.color }}
                      _visited={{ color: calendarButtonStyles.color }}
                    >
                      {translations.whereToStay}
                    </LinkV2>
                  </ButtonV2>
                )}
              </>
            )}
            {/* Get Directions Button Shows in the top content on mobile */}
            {item?.location && !isMobile ? (
              <ButtonV2
                {...testIds.getDirections.prop}
                variant="ghost"
                shape="rounded"
                {...calendarButtonStyles}
                _hover={{ backgroundColor: 'transparent', color: calendarButtonStyles.color }}
                paddingX={0}
                size="sm"
              >
                <LinkV2
                  href={getGoogleMapsSearchLink(item?.location)}
                  isExternal={true}
                  color={calendarButtonStyles.color}
                  _hover={{ color: calendarButtonStyles.color }}
                  _visited={{ color: calendarButtonStyles.color }}
                >
                  {translations.getDirections}
                </LinkV2>
              </ButtonV2>
            ) : null}
          </GuestSiteTypographyOverride>
        </Flex>
      </InfoBlock.Group>
    </Box>
  );
};

const getCalendarButtonProps = (accentColor?: string) => {
  if (accentColor) {
    return getButtonStylesWithAccentColor(accentColor);
  }
  return {
    borderColor: 'mono3',
    color: 'mono14',
    _hover: { backgroundColor: 'mono2', borderColor: 'mono3', color: 'mono14' },
    _active: { backgroundColor: 'mono3', borderColor: 'mono3', color: 'mono14' },
    _disabled: {
      backgroundColor: 'mono3',
      borderColor: 'mono3',
      color: 'mono8',
      cursor: 'not-allowed',
    },
  };
};

const SecondaryBlock: React.FC<
  Readonly<{
    isMobile?: boolean;
    timeframe?: string;
    eventHandle: string;
    description?: string;
    showWhereToStayLink: boolean;
    item?: EventScheduleFragment;
    eventNameMarkup?: JSX.Element;
    virtualEventMarkup?: JSX.Element;
    translations: ScheduleListTranslations;
    styleProps: VariantStyles['secondaryBlock'];
    testIds: ScheduleListTestIds['listItemTestIds'];
    onCalendarClicked?: (title: string, type: string) => void;
  }>
> = ({ eventNameMarkup, styleProps, testIds, timeframe, translations, virtualEventMarkup, item, onCalendarClicked, description, isMobile, showWhereToStayLink }) => {
  const showDurationBlock = !!(eventNameMarkup || timeframe);
  const { pageFontColor, accentColor } = useColorPaletteProvider();
  const textColorProps = pageFontColor ? { color: pageFontColor } : {};
  const calendarButtonStyles = useMemo(() => getCalendarButtonProps(accentColor), [accentColor]);
  const showAddToCalendarButton = !showWhereToStayLink && timeframe && item?.startTime?.timestamp;
  const showGetDirectionsButton = isMobile && item?.location;

  if (!item) {
    return null;
  }

  return (
    <SpacingStack
      {...testIds.secondaryBlock.prop}
      {...styleProps.container}
    >
      {showDurationBlock && (
        <>
          <Box {...styleProps.durationBlockContainer}>
            {eventNameMarkup}
            {timeframe && (
              <GuestSiteTypographyOverride override={label6ToCaptionOverride}>
                <TextV2
                  {...testIds.timeframe.prop}
                  typographyVariant="label6"
                  {...styleProps.durationBlock}
                  {...textColorProps}
                  css={{ whiteSpace: 'pre-line' }}
                >
                  {timeframe}
                </TextV2>
              </GuestSiteTypographyOverride>
            )}
          </Box>
          {/* without a start time there is no reason to add to calendar */}
          {(showAddToCalendarButton || showGetDirectionsButton) && (
            <Flex columnGap={2}>
              {showAddToCalendarButton && (
                <GuestSiteTypographyOverride override={button2ToButtonsOverride}>
                  <AddToCalendar
                    start={item.startTime?.timestamp}
                    end={item.endTime?.timestamp}
                    title={item.name}
                    location={item.location ?? ''}
                    description={description}
                    handleClickAddToCalendar={onCalendarClicked}
                  >
                    <ButtonV2
                      {...testIds.addToCalendar.prop}
                      startIcon={() => <AddToCalendarIcon />}
                      variant="outline"
                      shape="rounded"
                      size={'lg'}
                      {...calendarButtonStyles}
                      {...styleProps.calendarBlock}
                    >
                      {isMobile && item.location ? translations.addToCal : translations.addToCalendar}
                    </ButtonV2>
                  </AddToCalendar>
                </GuestSiteTypographyOverride>
              )}

              {showGetDirectionsButton && (
                <GuestSiteTypographyOverride override={body3ToButtonsOverride}>
                  <ButtonV2
                    {...testIds.getDirections.prop}
                    startIcon={() => <Direction />}
                    variant="outline"
                    shape="rounded"
                    size={'lg'}
                    {...calendarButtonStyles}
                    {...styleProps.calendarBlock}
                  >
                    <LinkV2
                      href={getGoogleMapsSearchLink(item.location ?? '')}
                      isExternal={true}
                      color={calendarButtonStyles.color}
                      textDecoration={'none'}
                      _hover={{ color: calendarButtonStyles.color }}
                      _visited={{ color: calendarButtonStyles.color }}
                    >
                      {translations.getDirections}
                    </LinkV2>
                  </ButtonV2>
                </GuestSiteTypographyOverride>
              )}
            </Flex>
          )}
        </>
      )}
      {virtualEventMarkup}
    </SpacingStack>
  );
};

const ScheduleListItem: React.FC<{
  eventId: string;
  eventHandle: string;
  item: EventScheduleFragment;
  styleProps: VariantStyles;
  isHeaderInSecondaryBlock: boolean;
  translations: ScheduleListTranslations;
  showTimeZone: boolean;
  isToBeAnnounced: boolean;
  onAddressClicked?: (title: string) => void;
  onCalendarClicked?: (title: string, type: string) => void;
  onVirtualEventBlockClicked?: () => void;
  isAccommodationPageVisible: boolean;
  onClickWhereToStay: () => void;
}> = React.memo(
  ({
    eventId,
    eventHandle,
    item,
    isHeaderInSecondaryBlock,
    isToBeAnnounced = false,
    translations,
    styleProps,
    showTimeZone,
    onAddressClicked,
    onCalendarClicked,
    onVirtualEventBlockClicked,
    isAccommodationPageVisible,
    onClickWhereToStay,
  }) => {
    const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } }, false);
    const { bookYourStay } = useHotelRoutePaths();
    const { id, startTime, endTime, name, virtualEventLink, description, dressCode, location, placeId, showMap } = item;
    const startTimeMs = startTime?.milliseconds;
    const endTimeMs = endTime?.milliseconds;
    const timezoneIANA = startTime?.timezone;
    const timeframe = translations.timeframeTz(showTimeZone, startTimeMs, endTimeMs, timezoneIANA);
    const { listItemTestIds: testIds } = useTestIds(id);
    const [showWhereToStayLink, loadingShowWhereToStayLink] = useIsFeatureEnabled('guestScheduleWhereToStayLinkEnabled');
    const [hideWhereToStayLink, loadingWhereToStayLinkUserConsentKey] = useIsFeatureEnabled({
      consentKey: 'scheduleOptOutWhereToStayLink',
      skip: !showWhereToStayLink,
    });

    const shouldShowWhereToStayButton = showWhereToStayLink && !hideWhereToStayLink && !loadingWhereToStayLinkUserConsentKey;
    const shouldIncludeAccommodationsLink = shouldShowWhereToStayButton && !loadingShowWhereToStayLink;

    const calendarDescription = useMemo(
      () => translations.description(bookYourStay, item.description, item.dressCode, eventHandle, config.clientUri, shouldIncludeAccommodationsLink),
      [eventHandle, translations, bookYourStay, item.dressCode, item.description, shouldIncludeAccommodationsLink],
    );
    const { scaleFontSize } = useScaleGuestSiteFontSize();

    const eventNameMarkup = (
      <GuestSiteTypographyOverride override={hed3ToSubHeadingOverride}>
        <TextV2
          {...testIds.eventName.prop}
          marginTop={{ sm4: '-1.5px' }}
          marginBottom={'0.875rem'}
          typographyVariant="hed3"
          lineHeight={1.35}
          fontWeight={500}
          fontSize={scaleFontSize(pxToRem(20), Category.SUB_HEADING)}
          // minHeight is used to ensure the placement of content below it will remain in the same position even if name is empty
          minHeight={`${Number.parseFloat(scaleFontSize(pxToRem(20), Category.SUB_HEADING)) * 1.35}rem`}
          wordBreak="break-word"
          textAlign={['center', 'center', 'inherit']}
        >
          {name}
        </TextV2>
      </GuestSiteTypographyOverride>
    );
    const virtualEventMarkup = virtualEventLink ? (
      <StyledVirtualEventContainer
        __css={styles.virtualEventContainer}
        {...testIds.livestream.prop}
      >
        <VirtualEventBlock
          onVirtualEventBlockClicked={onVirtualEventBlockClicked}
          startTime={startTime}
          endTime={endTime}
          virtualEvent={virtualEventLink}
        />
      </StyledVirtualEventContainer>
    ) : undefined;

    const isSecondaryBlockHasContent = !!(virtualEventLink || location || dressCode || description || timeframe);

    return (
      <ContentColumns
        centered={true}
        role="listitem"
        key={id}
        {...{ ...styleProps.scheduleDayItemContainer, ...(!isSecondaryBlockHasContent ? styles.columnReverseContainer : {}) }}
      >
        {!isToBeAnnounced && (
          <ContentColumns.Column {...styleProps.secondaryBlockColumns}>
            <SecondaryBlock
              eventHandle={eventHandle}
              eventNameMarkup={isHeaderInSecondaryBlock ? eventNameMarkup : undefined}
              styleProps={isSecondaryBlockHasContent ? styleProps.secondaryBlock : variantStyles['stack'].secondaryBlock}
              testIds={testIds}
              item={item}
              timeframe={isToBeAnnounced ? undefined : timeframe}
              translations={translations}
              virtualEventMarkup={isHeaderInSecondaryBlock ? virtualEventMarkup : undefined}
              onCalendarClicked={onCalendarClicked}
              description={calendarDescription}
              isMobile={isMobile}
              showWhereToStayLink={showWhereToStayLink}
            />
          </ContentColumns.Column>
        )}

        <ContentColumns.Column {...styleProps.primaryBlockColumns}>
          <PrimaryBlock
            title={name}
            description={description}
            dressCode={dressCode}
            eventNameMarkup={isHeaderInSecondaryBlock && !isToBeAnnounced ? undefined : eventNameMarkup}
            isToBeAnnounced={isToBeAnnounced}
            translations={translations}
            location={location}
            placeId={placeId}
            showMap={showMap}
            styleProps={styleProps.primaryBlock}
            testIds={testIds}
            virtualEventMarkup={isHeaderInSecondaryBlock ? undefined : virtualEventMarkup}
            onAddressClicked={onAddressClicked}
            calendarDescription={calendarDescription}
            onCalendarClicked={onCalendarClicked}
            isMobile={isMobile}
            timeframe={isToBeAnnounced ? undefined : timeframe}
            item={item}
            eventHandle={eventHandle}
            eventId={eventId}
            isAccommodationPageVisible={isAccommodationPageVisible}
            onClickWhereToStay={onClickWhereToStay}
            shouldShowWhereToStayButton={shouldShowWhereToStayButton}
          />
        </ContentColumns.Column>
      </ContentColumns>
    );
  },
);

const ScheduleListContent = React.forwardRef<HTMLDivElement, ScheduleListContentProps>((props, ref) => {
  const {
    children,
    eventHandle,
    shouldDisplayUnlockButton,
    handleUnlockDialogClick,
    unlockButtonText,
    formattedSchedule,
    tbaSchedule,
    layout,
    variant = 'inline',
    eventId,
    isAccommodationPageVisible,
    onClickWhereToStay,
    ...restProps
  } = props;
  const isMdOrSmScreen = useMediaQuery(theme => theme.mediaQueries.down({ breakpointAlias: 'sm3' }));
  const isStackVariant = variant === 'stack';
  const isHeaderInSecondaryBlock = isMdOrSmScreen || isStackVariant;
  const { pageFontColor: textColor } = useColorPaletteProvider();
  const borderBottomOverride = textColor ? { borderBottomColor: textColor } : {};

  const styles = variantStyles[variant];
  const translations = useTranslations();
  const telemetry = useScheduleListTelemetry();
  const { formatDate } = useLocaleContext();

  const handleAddressClicked = useCallback(
    scheduleTitle => {
      telemetry.scheduleAddressClicked(scheduleTitle);
    },
    [telemetry],
  );

  const handleAddToCalendar = useCallback(
    (scheduleTitle: string, provider: string) => {
      telemetry.scheduleAddToCalendarClicked(scheduleTitle, provider);
    },
    [telemetry],
  );

  const handleVirtualEventClicked = useCallback(
    (scheduleTitle: string) => {
      telemetry.scheduleVirtualEventClicked(scheduleTitle);
    },
    [telemetry],
  );

  const _renderScheduleGroup = (key: string | number, header: string, items: ReadonlyArray<EventScheduleFragment>, index: number) => {
    if (!items.length) {
      return null;
    }
    const isToBeAnnounced = key === 'tba';
    const showTimeZone = items.reduce((resultSet, item) => resultSet.add(item.timezone), new Set()).size > 1;
    const headerMarginTop = shouldDisplayUnlockButton ? (index === 0 ? 9 : 7) : 0;

    return (
      <div
        key={key}
        data-testid={`schedule-group-${key}`}
      >
        <Box
          as="section"
          {...styles.container}
        >
          <Box
            as="header"
            {...styles.header}
            marginTop={headerMarginTop}
            {...borderBottomOverride}
          >
            <GuestSiteTypographyOverride override={hed4ToSubHeadingOverride}>
              <TextV2
                data-testid={`groupheader-${key}`}
                typographyVariant="hed4"
                {...styles.headerText}
              >
                {header}
              </TextV2>
            </GuestSiteTypographyOverride>
          </Box>
          <Flex
            flexDirection="column"
            role="list"
            rowGap={8}
          >
            {items.map(item => {
              return (
                <ScheduleListItem
                  key={item.id}
                  eventId={eventId}
                  eventHandle={eventHandle}
                  item={item}
                  isHeaderInSecondaryBlock={isHeaderInSecondaryBlock}
                  isToBeAnnounced={isToBeAnnounced}
                  styleProps={styles}
                  translations={translations}
                  showTimeZone={showTimeZone}
                  onAddressClicked={handleAddressClicked}
                  onCalendarClicked={handleAddToCalendar}
                  onVirtualEventBlockClicked={() => handleVirtualEventClicked(item.name)}
                  isAccommodationPageVisible={isAccommodationPageVisible}
                  onClickWhereToStay={onClickWhereToStay}
                />
              );
            })}
          </Flex>
        </Box>
      </div>
    );
  };

  return (
    <Box
      ref={ref}
      {...restProps}
      {...styles.events}
    >
      <>
        <Flex justifyContent={'center'}>
          {shouldDisplayUnlockButton && (
            <GuestSiteTypographyOverride override={button2ToButtonsOverride}>
              <ButtonV2
                onClick={handleUnlockDialogClick}
                variant="outline"
                shape="rounded"
              >
                {unlockButtonText}
              </ButtonV2>
            </GuestSiteTypographyOverride>
          )}
        </Flex>
        <SpacingStack {...styles.eventsStack}>
          {formattedSchedule.map(({ date: groupDate, items }, i) => {
            const groupDateHeader = formatDate(groupDate.timestamp, 'weekday\ndd MMMM yyyy', { titlecase: true, timezone: groupDate.timezone }) ?? '';
            return _renderScheduleGroup(`${groupDate.milliseconds}-${i}`, groupDateHeader, items, i);
          })}
          {_renderScheduleGroup('tba', translations.toBeAnnounced, tbaSchedule, formattedSchedule.length)}
        </SpacingStack>
      </>
    </Box>
  );
});

const ScheduleList = React.forwardRef<HTMLDivElement, ScheduleListProps>((props, ref) => {
  const { eventId, eventHandle } = props;
  const { layout } = useLayout();
  const { formattedSchedule, shouldDisplayUnlockButton, loading, tbaSchedule, handleUnlockDialogClick, unlockButtonText } = useScheduleListController(eventHandle);

  const eventDisplayName = useEventDisplayName();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { hasVisibleAccommodationsEventPage, error, hasVisibleTravelEventPage, hasTravelAccommodationListings, isResolved, matchedCustomPageSlug } = useEventAccommodationsCheck({
    eventHandle,
  });

  // Determines if accommodation-related content should be shown
  const isAccommodationPageVisible =
    // Ensure API call completed successfully
    isResolved &&
    !error &&
    // Accommodations event page is visible
    (hasVisibleAccommodationsEventPage ||
      // Travel event page is visible and contains accommodation listings
      (hasVisibleTravelEventPage && hasTravelAccommodationListings) ||
      // A custom page matches an accommodation-related slug
      !!matchedCustomPageSlug);

  if (loading) return <div style={{ height: '300px' }}></div>; // height set to prevent divs from jumping around while page loads

  return (
    <TelemetryProvider context={{ layout, eventId }}>
      <AccommodationsConsoleConfigProvider>
        <ScheduleListContent
          layout={layout}
          {...props}
          ref={ref}
          tbaSchedule={tbaSchedule}
          formattedSchedule={formattedSchedule}
          handleUnlockDialogClick={handleUnlockDialogClick}
          shouldDisplayUnlockButton={shouldDisplayUnlockButton}
          unlockButtonText={unlockButtonText}
          isAccommodationPageVisible={isAccommodationPageVisible}
          onClickWhereToStay={onOpen}
        />
        <AccommodationsConsoleDialog
          isOpen={isOpen}
          onClose={onClose}
          eventHandle={eventHandle}
          viewMode="overlay"
          eventName={eventDisplayName}
        />
      </AccommodationsConsoleConfigProvider>
    </TelemetryProvider>
  );
});
ScheduleList.displayName = 'ScheduleList';

export { ScheduleList };
